import React, { useState } from "react";
import { AlertTriangle, X, Mail, ShieldOff, Shield, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import { adminRecoveryApi, formatApiError } from "@/lib/apiClient";

const META = {
  "reset-2fa": {
    label: "Reset 2FA",
    icon: ShieldOff,
    tone: "warning",
    headline: "Reset two-factor authentication",
    description: "This wipes the user's enrolled 2FA methods (SMS, Voice, TOTP, Email) AND their backup codes. The user will be forced to re-enroll 2FA on their next login. Their password is unchanged.",
  },
  "send-password-reset": {
    label: "Send Password Reset",
    icon: Mail,
    tone: "success",
    headline: "Send password reset link",
    description: "Emails the user a one-time magic link (valid for 1 hour, single use) that lets them set a new password. 2FA is unchanged. You will NOT see the link — only the user receives it.",
  },
  "send-password-reset-sms": {
    label: "Send Password Reset (SMS)",
    icon: MessageSquare,
    tone: "success",
    headline: "Send password reset SMS code",
    description: "Texts the user a 6-digit code (valid for 15 minutes) that lets them reset their password. 2FA is unchanged. You will NOT see the code — only the user receives it. Requires the user to have a verified phone number on file.",
  },
  "full-recovery": {
    label: "Full Recovery",
    icon: AlertTriangle,
    tone: "danger",
    headline: "Full account recovery",
    description: "Combines both actions atomically: wipes 2FA state AND dispatches a password-reset code via the chosen channel. One audit entry. One message to the user. Use when the user has lost access to both their password and their 2FA device.",
  },
};

const toneClass = {
  warning: { border: "border-warning/40", icon: "text-warning", bg: "bg-warning/10" },
  success: { border: "border-success/40", icon: "text-success", bg: "bg-success/10" },
  danger:  { border: "border-danger/40",  icon: "text-danger",  bg: "bg-danger/10"  },
};

const RecoveryActionsModal = ({ user, action, onClose, onCompleted }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!user || !action) return null;
  const meta = META[action];
  const tone = toneClass[meta.tone];
  const Icon = meta.icon;
  const reasonOk = reason.trim().length >= 10;

  const submit = async () => {
    if (!reasonOk) { setError("Reason must be at least 10 characters."); return; }
    setError("");
    setSubmitting(true);
    try {
      let res;
      if (action === "reset-2fa")             res = await adminRecoveryApi.reset2fa(user.id, reason);
      else if (action === "send-password-reset") res = await adminRecoveryApi.sendPasswordReset(user.id, reason);
      else if (action === "send-password-reset-sms") res = await adminRecoveryApi.sendPasswordResetSms(user.id, reason);
      else if (action === "full-recovery")    res = await adminRecoveryApi.fullRecovery(user.id, reason);
      setResult(res);
      toast.success(`${meta.label} completed for ${user.email}.`);
      onCompleted?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="recovery-modal"
    >
      <div
        className="w-full max-w-md bg-bg-secondary border border-border-default rounded-[12px] shadow-elevated overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-6 py-5 border-b border-border-default">
          <div className={`shrink-0 w-10 h-10 rounded-[8px] border ${tone.border} ${tone.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${tone.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[16px] font-bold text-white">{meta.headline}</h3>
            <p className="text-[12px] text-text-muted mt-0.5 truncate">Target: {user.email}</p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-white" data-testid="recovery-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-[13px] text-text-secondary leading-relaxed">{meta.description}</p>

          {!result ? (
            <>
              <label className="block">
                <div className="text-[11px] font-semibold text-text-muted tracking-wider uppercase mb-1.5">
                  Reason <span className="text-danger">*</span>
                  <span className="text-text-muted font-normal normal-case tracking-normal ml-1">
                    (min 10 chars — logged for audit)
                  </span>
                </div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Support ticket #4213 — user lost access to authenticator app and requested manual reset."
                  rows={3}
                  className="w-full bg-bg-tertiary border border-border-default text-white text-[13px] rounded-[8px] px-3 py-2 placeholder:text-text-muted focus:outline-none focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]"
                  data-testid="recovery-reason-input"
                />
                <div className={`mt-1 text-[11px] ${reasonOk ? "text-success" : "text-text-muted"}`}>
                  {reason.trim().length}/10 characters minimum
                </div>
              </label>

              {error && (
                <div className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2" data-testid="recovery-modal-error">
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className={`${tone.bg} border ${tone.border} rounded-[8px] p-4 space-y-2`} data-testid="recovery-modal-result">
              <div className="flex items-center gap-2">
                <Shield className={`w-4 h-4 ${tone.icon}`} />
                <div className="text-[13px] font-semibold text-white">Action completed</div>
              </div>
              <div className="text-[12px] text-text-secondary leading-relaxed">
                Audit log entry: <span className="font-mono text-white">{result.audit_log_id}</span>
                {result.token_expires_at && (
                  <>
                    <br />Magic link expires: <span className="font-mono text-white">{new Date(result.token_expires_at).toLocaleString()}</span>
                  </>
                )}
                <br />The user has been notified by email.
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-2 bg-bg-primary/30">
          {!result ? (
            <>
              <JeaButton size="md" variant="outline-neutral" onClick={onClose} disabled={submitting} data-testid="recovery-modal-cancel">
                Cancel
              </JeaButton>
              <JeaButton size="md" onClick={submit} disabled={!reasonOk || submitting} loading={submitting} data-testid="recovery-modal-submit">
                {meta.label}
              </JeaButton>
            </>
          ) : (
            <JeaButton size="md" onClick={onClose} data-testid="recovery-modal-done">
              Done
            </JeaButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoveryActionsModal;
